import { useRef, useState } from "react";
import SectionContent from "./sectionContent.jsx";

/**
 * Generic carousel component with two responsive layouts:
 *
 * Mobile  (<lg): full-width image inside SectionContent padding, title + subtitle
 *               below the image, horizontal dash indicators, swipe to navigate.
 *
 * Desktop (≥lg): centered active slide capped at `desktopMaxImageWidth` (default
 *               1720px) with a fixed 2:1 aspect ratio (matches XPENG p7+ design,
 *               e.g. 1720×860). Adjacent slides are revealed at reduced opacity
 *               in the symmetric side gutters via `overflow: hidden` on a wider
 *               track, scaling with viewport. Arrow buttons sit in the gap
 *               between active and adjacent slides.
 *
 * Recommended source image: 1920×960 or 2400×1200 (2:1). Images are rendered as
 * `background-image` with `bg-cover bg-center`, so any 2:1 image will fit
 * cleanly; non-2:1 images will be cropped to the 2:1 frame (top/bottom for 16:9
 * sources).
 *
 * Props:
 *   items                 – Array<{ imageUrl: string, title: ReactNode, subtitle: ReactNode }>
 *   desktopMaxImageWidth  – Max width (in px) of the active desktop slide (default 1720)
 *   lightBackground       – Use dark text variants when rendered on a light background
 */
export default function Carousel({ items, desktopMaxImageWidth = 1720, lightBackground = false }) {
  const [idx, setIdx] = useState(0);
  const touchStart = useRef(null);

  if (!items?.length) return null;

  // ── Touch / swipe ──────────────────────────────────────────────────────────
  function onTouchStart(e) {
    touchStart.current = e.touches[0].clientX;
  }
  function onTouchEnd(e) {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setIdx((p) => Math.min(p + 1, items.length - 1));
      else setIdx((p) => Math.max(p - 1, 0));
    }
    touchStart.current = null;
  }

  // ── Desktop layout ─────────────────────────────────────────────────────────
  // Active slide is centered. Slide width caps at `desktopMaxImageWidth` (px)
  // and otherwise scales as ~75vw. Gap scales with viewport. Adjacent slides
  // become visible (at opacity 0.4) in the side gutters thanks to
  // `overflow: hidden` on the outer wrapper.
  // Min gap (4.5rem = 72px) leaves room for the 56px circular arrow buttons
  // (w-14 h-14) plus breathing space, so arrows never overlap the active image.
  const desktopVars = {
    "--carousel-slide-w": `min(${desktopMaxImageWidth}px, 75vw)`,
    "--carousel-gap": "clamp(4.5rem, 5vw, 7rem)",
    "--carousel-idx": idx,
  };

  const current = items[idx];

  const Placeholder = ({ className, style }) => (
    <div className={`bg-gray-200 ${className}`} style={style} />
  );

  return (
    <>
      {/* ── MOBILE ────────────────────────────────────────────────────────── */}
      <div className="block lg:hidden" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <SectionContent className="py-8">
          {/* Image */}
          {current.imageUrl ? (
            <div
              className="w-full aspect-[2/1] bg-cover bg-center rounded-xl"
              style={{ backgroundImage: `url(${current.imageUrl})` }}
            />
          ) : (
            <Placeholder className="w-full aspect-[2/1] rounded-xl" />
          )}

          {/* Title */}
          <p className="text-2xl font-bold text-text-strong mt-5 leading-tight">{current.title}</p>

          {/* Subtitle */}
          <p className="text-text-muted text-base mt-2">{current.subtitle}</p>

          {/* Dash indicators */}
          <div className="flex gap-2 mt-6">
            {items.map((item, i) => (
              <button
                key={`indicator-${item.imageUrl || i}`}
                type="button"
                onClick={() => setIdx(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === idx ? "bg-primary w-8" : "bg-gray-300 w-6"
                }`}
                aria-label={`Gå till bild ${i + 1}`}
              />
            ))}
          </div>
        </SectionContent>
      </div>

      {/* ── DESKTOP ───────────────────────────────────────────────────────── */}
      <div className="hidden lg:block">
        {/* Peek carousel track — full viewport width, overflow-hidden clips adjacent slides.
            Title + subtitle live inside each slide so they fade together with the image. */}
        <div
          className="relative z-10 overflow-hidden w-full"
          style={desktopVars}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{
              gap: "var(--carousel-gap)",
              paddingLeft: "calc((100% - var(--carousel-slide-w)) / 2)",
              paddingRight: "calc((100% - var(--carousel-slide-w)) / 2)",
              transform:
                "translateX(calc(-1 * var(--carousel-idx) * (var(--carousel-slide-w) + var(--carousel-gap))))",
            }}
          >
            {items.map((item, i) => {
              const slideStyle = {
                width: "var(--carousel-slide-w)",
                opacity: i === idx ? 1 : 0.4,
              };
              const isActive = i === idx;
              return (
                <div
                  key={`slide-${item.imageUrl || i}`}
                  className="flex-shrink-0 transition-opacity duration-500"
                  style={slideStyle}
                >
                  <p
                    className={`text-3xl md:text-4xl font-bold leading-tight text-center ${lightBackground ? "text-gray-900" : "text-white"}`}
                  >
                    {item.title}
                  </p>
                  <p
                    className={`text-lg mt-1 mb-6 text-center ${lightBackground ? "text-gray-600" : "text-white/70"}`}
                  >
                    {item.subtitle}
                  </p>
                  <div className="relative w-full aspect-[2/1]">
                    {item.imageUrl ? (
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${item.imageUrl})` }}
                      />
                    ) : (
                      <Placeholder className="absolute inset-0" />
                    )}

                    {/* Arrows live inside the active slide's image area so they're
                        always vertically centered on the image regardless of title height. */}
                    {isActive && idx > 0 && (
                      <button
                        type="button"
                        onClick={() => setIdx((p) => Math.max(p - 1, 0))}
                        className={`absolute top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-transform hover:scale-110 ${lightBackground ? "bg-white/90 text-gray-800 hover:bg-white" : "bg-black/50 text-white hover:bg-black/70"}`}
                        style={{
                          left: "calc(-1 * var(--carousel-gap) / 2)",
                          transform: "translate(-50%, -50%)",
                        }}
                        aria-label="Föregående"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-7 h-7"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <title>Föregående</title>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                    )}
                    {isActive && idx < items.length - 1 && (
                      <button
                        type="button"
                        onClick={() => setIdx((p) => Math.min(p + 1, items.length - 1))}
                        className={`absolute top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-14 h-14 rounded-full shadow-lg transition-transform hover:scale-110 ${lightBackground ? "bg-white/90 text-gray-800 hover:bg-white" : "bg-black/50 text-white hover:bg-black/70"}`}
                        style={{
                          left: "calc(100% + var(--carousel-gap) / 2)",
                          transform: "translate(-50%, -50%)",
                        }}
                        aria-label="Nästa"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-7 h-7"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <title>Nästa</title>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2.5}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
